import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { analytics } from '@/utils/analytics';
import { isDDEnv, isWHEnv } from '@/utils/helper';

import Text from '@/atoms/Text/Text';
import Icon from '@/atoms/Icon/Icon';
import ButtonIcon from '@/atoms/ButtonIcon/ButtonIcon';
import ClipAnimation from '@/atoms/ClipAnimation/ClipAnimation';
import Checkbox from '@/atoms/Checkbox/Checkbox';

function Search({ open, closeSearch, tagGroupsWithTags }) {
  const exploreDivRef = useRef(null);
  const inputRef = useRef(null);
  const [canStartTextAnimation, setCanStartTextAnimation] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');
  const [tagSearch, setTagSearch] = useState([]);
  const [mobileAccordionIsOpen, setMobileAccordionIsOpen] = useState(null);
  const router = useRouter();
  const isKorean = router?.locale === 'ko-KR';

  const hideExplore = () => {
    closeSearch();
  };

  //on open, run clip animation and set input focus
  useEffect(() => {
    const exploreDiv = exploreDivRef.current;

    const handleTransitionEnd = (e) => {
      if (
        e.target.classList.contains('aaco-search') &&
        e.propertyName === 'opacity'
      ) {
        if (open && inputRef.current) {
          handleClear();
          setCanStartTextAnimation(true);
          inputRef.current.focus();
        } else {
          setCanStartTextAnimation(false);
        }
      }
    };

    exploreDiv.addEventListener('transitionend', handleTransitionEnd);

    return () =>
      exploreDiv.removeEventListener('transitionend', handleTransitionEnd);
  }, [open]);

  const handleTextInput = (e) => {
    setTextInputValue(e.target.value);
  };

  const handleCheckboxChange = (e, tag) => {
    if (e.target.checked) {
      setTagSearch([...tagSearch, tag]);
    } else {
      setTagSearch(tagSearch.filter((i) => i.id !== tag.id));
    }
  };

  const handleClear = () => {
    setTextInputValue('');
    setTagSearch([]);
    setMobileAccordionIsOpen(null);
  };

  const handleClickMobileAccordionHeader = (tagGroupId) => {
    if (window.innerWidth < 768) {
      setMobileAccordionIsOpen(tagGroupId);
    }
  };

  const handleEnterKey = (e) => {
    if (e?.nativeEvent.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleSearchSubmit = (e) => {
    //construct new urlParams object

    if (tagSearch.length > 0) {
      const searchTags = tagSearch.map((i) => i.slug).join(',');
      router.push(
        `/search?t=${searchTags}&fromSearch=true`,
        `/search?t=${searchTags}`
      );
    }
    if (textInputValue.length > 0) {
      router.push(
        `/search?q=${textInputValue}&fromSearch=true`,
        `/search?q=${textInputValue}`
      );
    }

    hideExplore();
  };

  return (
    <div
      ref={exploreDivRef}
      className={`aaco-search absolute top-0 z-50 left-0 w-full h-screen overflow-y-scroll bg-light-warm-grey dd-en:bg-progressive-blue dd-kr:bg-progressive-blue text-acacia transition-all duration-300 ${
        open ? 'visible opacity-100' : 'invisible opacity-0'
      }`}
    >
      <div className={`container mx-auto mt-8 ${isDDEnv() ? ' text-white' : ''}`}>
        <div className="flex justify-end items-center">
          <div className="cursor-pointer" onClick={hideExplore}>
            <Icon
              name="cross"
              className="dd-kr:w-4 dd-en:w-4 dd-kr:h-4 dd-en:h-4"
            />
          </div>
        </div>
        <div className="mt-[93px] dd-en:mt-24 dd-kr:mt-24 mb-[96px] md:mb-[90px] dd-en:mb-20 dd-kr:mb-20">
          <ClipAnimation startAnimation={canStartTextAnimation}>
            <Text
              tag="tag"
              style="text-acacia dd-en:text-actual-dd-gold dd-kr:text-actual-dd-gold dd-kr:font-small dd-kr:text-small dd-en:font-small dd-en:text-small"
            >
              {isKorean ? '검색하기' : 'Search'}
            </Text>
          </ClipAnimation>
          <ClipAnimation
            startAnimation={canStartTextAnimation}
            fullWidth={true}
          >
            <div
              className={`flex items-center border-b-1 border-acacia relative dd-kr:pt-[10px] dd-en:pt-[10px] md:dd-kr:pt-[15px] md:dd-en:pt-[15px] dd-kr:pb-[15px] dd-en:pb-[15px] md:dd-kr:pb-5 md:dd-en:pb-5 ${
                textInputValue?.length
                  ? 'dd-kr:border-actual-dd-gold dd-en:border-actual-dd-gold'
                  : 'dd-kr:border-80-white-on-dark-blue dd-en:border-80-white-on-dark-blue'
              }`}
            >
              <input
                ref={inputRef}
                value={textInputValue}
                onKeyDown={handleEnterKey}
                onInput={handleTextInput}
                placeholder={
                  isKorean ? '무엇이든 찾아보세요' : "I'm looking for..."
                }
                className={`w-full font-heading pt-4 pb-[14px] dd-en:py-2 dd-kr:py-2 md:py-0 font-h4-mobile dd-kr:font-h5-mobile dd-en:font-h5-mobile md:font-h2 md:dd-kr:font-h2 md:dd-en:font-h2 text-h4-mobile dd-en:text-h5-mobile dd-kr:text-h5-mobile md:dd-kr:text-h2 md:dd-en:text-h2 md:text-h2 bg-transparent outline-none placeholder:text-acacia placeholder:dd-kr:text-40-white-on-dark-blue placeholder:dd-en:text-40-white-on-dark-blue transition-opacity duration-1000`}
              />
              <div className="absolute right-0 flex gap-x-[14px] md:gap-x-6 dd-en:gap-x-6 dd-kr:gap-x-6 items-center">
                <div
                  onClick={handleClear}
                  className={`cursor-pointer transition-all duration-300 dd-kr:hidden dd-en:hidden ${
                    textInputValue.length || tagSearch.length
                      ? 'visible opacity-100'
                      : 'invisible opacity-0'
                  } `}
                >
                  <Text
                    tag="tag"
                    style="text-acacia dd-kr:text-actual-dd-gold dd-en:text-actual-dd-gold"
                  >
                    Clear
                  </Text>
                </div>
                <ButtonIcon
                  icon="arrow-right"
                  iconStyle={`${
                    textInputValue.length || tagSearch.length
                      ? 'text-white'
                      : 'text-acacia'
                  } dd-kr:text-actual-dd-gold dd-en:text-actual-dd-gold`}
                  width={11}
                  height={11}
                  style={`transition-all duration-1000 hover:shadow-inner ${
                    textInputValue.length || tagSearch.length
                      ? 'border-acacia bg-acacia drop-shadow '
                      : 'border-acacia'
                  } aaco-search-icon dd-kr:bg-transparent dd-en:bg-transparent`}
                  onClick={handleSearchSubmit}
                />
              </div>
            </div>
          </ClipAnimation>
        </div>
        <div
          className={`transition-opacity duration-1000 dd-en:hidden dd-kr:hidden`}
        >
          <ClipAnimation startAnimation={canStartTextAnimation}>
            <Text tag="h6" style="dd-kr:text-acacia dd-en:text-acacia text-black">
              Explore our content
            </Text>
          </ClipAnimation>
          <div className="block mt-8 md:hidden dd-kr:hidden dd-en:hidden">
            <Text tag="tag" style="text-acacia text-[10px] leading-[13px]">
              EXPLORE BY:
            </Text>
          </div>
          <div className="flex flex-wrap gap-x-20 gap-y-3 mt-1 md:mt-6 dd-en:mt-6 dd-kr:mt-6">
            {tagGroupsWithTags?.length > 0 &&
              tagGroupsWithTags.map((i) => (
                <div
                  key={i.id}
                  className={`w-full md:w-auto border-b md:border-b-0  ${
                    mobileAccordionIsOpen === i.id
                      ? 'pb-3 dd-en:pb-2 dd-kr:pb-2 md:pb-0 border-warm-grey'
                      : 'border-black'
                  }`}
                >
                  <div
                    className="flex justify-between items-center md:block mb-4 md:mb-3"
                    onClick={() => handleClickMobileAccordionHeader(i.id)}
                  >
                    <ClipAnimation startAnimation={canStartTextAnimation}>
                      <Text tag="body" style="text-black uppercase">
                        {i.name}
                      </Text>
                    </ClipAnimation>
                    <Icon
                      name="chevron-down"
                      width={12}
                      height={12}
                      className={`md:hidden transition-transform duration-500 ${
                        mobileAccordionIsOpen === i.id && 'rotate-180'
                      }`}
                    />
                  </div>
                  <ul
                    className={`flex flex-col overflow-hidden md:overflow-auto md:h-auto ${
                      mobileAccordionIsOpen === i.id ? 'h-auto' : 'h-0'
                    }`}
                  >
                    {i?.tags?.length > 0 &&
                      i.tags.map((j, index) => (
                        <li
                          key={j.id}
                          className="mb-3 md:mb-[10px] dd-kr:mb-3 dd-en:mb-3"
                        >
                          <ClipAnimation startAnimation={canStartTextAnimation}>
                            <Checkbox
                              id={`chk-${i.slug}-${index + 1}`}
                              checked={tagSearch.some((i) => i.id === j.id)}
                              onChange={(e) => handleCheckboxChange(e, j)}
                            />
                            <label
                              htmlFor={`chk-${i.slug}-${index + 1}`}
                              className="font-text font-body text-acacia text-body ml-2"
                            >
                              {j.name}
                            </label>
                          </ClipAnimation>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

Search.propTypes = {
  open: PropTypes.bool,
  closeSearch: PropTypes.func,
  tagGroupsWithTags: PropTypes.array,
};

Search.defaultProps = {
  open: false,
  closeSearch: () => {},
  tagGroupsWithTags: [],
};

export default Search;
